import { Edge, Node } from 'reactflow';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  ConceptualSubLayerTypes,
  ContextualSubLayerTypes,
  InitiativesSubLayerTypes,
  SubLayersWithoutValidStatus,
  SubLayersWithScopeFilterOnly,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { filtersSubject } from '@context/ProjectFilters/FiltersSubject';
import useQuestionnaireResultStore from '@store/zustand/questionnaire-result';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import { checkScopeFilter } from '@utils/domains';

/**
 * Id-addressed view of the canvas graph, for lookups that happen outside React.
 *
 * The maps are built on read and cached against the identity of the array they
 * came from, so a write nothing looks up costs nothing. Correct because the
 * store replaces `nodes` and `edges` rather than mutating them.
 */
class CanvasMap {
  private cachedNodes: Node[] | null = null;

  private cachedNodeMap = new Map<string, Node>();

  private cachedRelations: Edge[] | null = null;

  private cachedRelationMap = new Map<string, Edge>();

  get nodes(): Node[] {
    return useStore.getState().nodes;
  }

  get relations(): Edge[] {
    return useStore.getState().edges;
  }

  get nodeMap(): Map<string, Node> {
    const { nodes } = this;

    if (nodes !== this.cachedNodes) {
      this.cachedNodes = nodes;
      this.cachedNodeMap = new Map(nodes.map((node) => [node.id, node]));
    }

    return this.cachedNodeMap;
  }

  get relationMap(): Map<string, Edge> {
    const { relations } = this;

    if (relations !== this.cachedRelations) {
      this.cachedRelations = relations;
      this.cachedRelationMap = new Map(
        relations.map((edge) => [edge.id, edge]),
      );
    }

    return this.cachedRelationMap;
  }

  isNodeHiddenByFilters(id: string) {
    const {
      domains,
      reviewStatus,
      validationStatus,
      cia,
      operationalStatuses,
      operatingModelStatuses,
      partners,
    } = filtersSubject.getValue();

    const { result: questionnaireResult } =
      useQuestionnaireResultStore.getState();

    const node = this.nodeMap.get(id);
    if (!node) return false;

    const isContextualOrConceptual = [
      ContextualSubLayerTypes.StrategicEnablers,
      ContextualSubLayerTypes.StrategicPriorities,
      ConceptualSubLayerTypes.BusinessAttributes,
      ConceptualSubLayerTypes.SecurityAttributes,
    ].includes(node.parentNode as ContextualSubLayerTypes);

    if (
      questionnaireResult &&
      node.type === NodesTypes.CustomNode &&
      [...CiaSubLayers, InitiativesSubLayerTypes.ChangeInitiatives].includes(
        node.parentNode as any,
      ) &&
      node.data.dto.is_reported === false
    ) {
      return true;
    }

    if (domains.length) {
      const isCustomNode = node.type === NodesTypes.CustomNode;

      const isDomainIncluded = checkScopeFilter(
        domains,
        node.data?.dto?.type,
        node.data?.dto?.domainNodes,
      );

      return isCustomNode && !isDomainIncluded;
    }

    if (reviewStatus.length) {
      const isScopeFilterOnly = SubLayersWithScopeFilterOnly.includes(
        node.parentNode as SubLayerTypes,
      );

      return (
        isScopeFilterOnly ||
        !(
          node.type !== NodesTypes.CustomNode || node.data?.dto?.review_required
        )
      );
    }

    if (validationStatus.length) {
      const nodeWithoutValidStatus = SubLayersWithoutValidStatus.includes(
        node.parentNode as SubLayerTypes,
      );

      const isVisible =
        node.type !== NodesTypes.CustomNode ||
        validationStatus.includes(node.data?.dto?.valid_status);

      return nodeWithoutValidStatus || !isVisible;
    }

    if (
      Object.values(cia).some((fields) =>
        Object.values(fields).some((answers) => answers?.length),
      )
    ) {
      if (node.type !== NodesTypes.CustomNode) return false;

      if (questionnaireResult) {
        const isCiaSublayer = CiaSubLayers.includes(node.parentNode as any);

        if (!isCiaSublayer) return node;
      }

      const isAssuranceMatch = isCriteriaFieldsMatch(
        cia.assurance_level,
        node.data.dto.cia?.assurance_level?.criterias,
      );

      const isCoverageMatch = isCriteriaFieldsMatch(
        cia.coverage,
        node.data.dto.cia?.coverage?.criterias,
      );

      const isApplicabilityMatch = isCriteriaFieldsMatch(
        cia.applicability,
        node.data.dto.cia?.applicability?.criterias,
      );

      const isReported = node.data.dto.is_reported !== false;

      const isAllMatch =
        isAssuranceMatch &&
        isCoverageMatch &&
        isApplicabilityMatch &&
        isReported;

      return !isAllMatch;
    }

    if (operationalStatuses.length) {
      if (isContextualOrConceptual) {
        return node.data.canvas.hiddenBA;
      }

      if (node.type !== NodesTypes.CustomNode) return false;

      if (questionnaireResult) {
        if (node.parentNode === InitiativesSubLayerTypes.ChangeInitiatives) {
          return false;
        }
      }

      const opStatus = node.data.dto?.meta_data?.operational_status;

      // The report only excludes "In Development" controls, so nodes without an
      // operational status must stay visible in the questionnaire result flow.
      const isStatusMatch =
        operationalStatuses.includes(opStatus) ||
        (Boolean(questionnaireResult) && !opStatus);

      return !isStatusMatch;
    }

    if (operatingModelStatuses.length) {
      if (node.type !== NodesTypes.CustomNode) return false;

      const isStatusMatch = operatingModelStatuses.includes(
        node.data.dto?.meta_data?.operational_model,
      );

      return !isStatusMatch;
    }

    if (partners.length) {
      if (node.type !== NodesTypes.CustomNode) return false;

      const isPartnerMatch = partners.some(
        (partner) => node.data.dto.partners?.includes(partner),
      );

      return !isPartnerMatch;
    }

    return false;
  }
}

export const canvasMap = new CanvasMap();
